import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  HiOutlineDocument,
  HiOutlineDownload,
  HiOutlineExternalLink,
  HiOutlineMicrophone,
  HiOutlineTrash,
  HiUpload,
} from 'react-icons/hi';
import { IoMdSend, IoMdSquare } from 'react-icons/io';
import { useHistory } from 'react-router-dom';

import { Context } from 'context/AuthContext';
import { GET_USUARIO } from 'services/Usuario';
import { appConst } from 'constants/appConstants';
import {
  GET_ULTIMO_BRIEFING_POR_USUARIO,
  POST_ENVIAR_BRIEFING,
  PROCESS_AI_EXTERNA,
  CONSULTAR_BRIEFING_AI_EXTERNA,
  TRANSCREVER_AUDIO,
} from 'services/BriefingAI';
import PainelSecaoConsulta from './PainelSecaoConsulta';
import PainelSecaoExterna from './PainelSecaoExterna';

import './styles.scss';
import PainelSecaoCadastroBriefing from './PainelSecaoCadastroBriefing';

const pickUsuarioId = (userData, localUser) => {
  if (userData?.userId != null) return userData.userId;
  if (localUser?.userId != null) return localUser.userId;
  return null;
};

const isImageFile = (file) => {
  if (file.type && file.type.startsWith('image/')) return true;
  // Windows/alguns SOs deixam type vazio; inferir pela extensão
  return /\.(jpe?g|png|gif|webp|bmp|svg|ico|heic|heif|avif)$/i.test(file.name);
};

const isImageFileName = (name) => {
  if (!name || typeof name !== 'string') return false;
  return /\.(jpe?g|png|gif|webp|bmp|svg|ico|heic|heif|avif)$/i.test(name);
};

/** Corpo às vezes vem como string JSON (ex.: Accept text/plain). */
const coerceAiBriefingResponseObject = (data) => {
  if (data == null) return null;
  if (typeof data === 'object') return data;
  if (typeof data === 'string') {
    const t = data.trim();
    if (!t) return null;
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  }
  return null;
};

/** Se o backend gravar escapes no texto (ex.: \u00ea literais após um parse). */
const decodeEmbeddedJsonEscapes = (s) => {
  if (typeof s !== 'string' || s === '') return s;
  return s
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, h) =>
      String.fromCharCode(parseInt(h, 16))
    )
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t');
};

const normalizeAiBriefingResponse = (data) => {
  const obj = coerceAiBriefingResponseObject(data);
  if (obj == null || typeof obj !== 'object') {
    return { message: '', error: '', url: '' };
  }
  const message = obj.message ?? obj.Message ?? '';
  const error = obj.error ?? obj.Error ?? '';
  const url = obj.url ?? obj.Url ?? '';
  return {
    message: decodeEmbeddedJsonEscapes(
      typeof message === 'string' ? message : String(message ?? '')
    ),
    error: decodeEmbeddedJsonEscapes(
      typeof error === 'string' ? error : String(error ?? '')
    ),
    url: typeof url === 'string' ? url : String(url ?? ''),
  };
};

const mapApiArquivosToItens = (arquivos) => {
  if (!Array.isArray(arquivos) || arquivos.length === 0) return [];
  return arquivos.map((a, idx) => {
    const nomeArquivo = a.nomeArquivo ?? a.NomeArquivo ?? `arquivo-${idx + 1}`;
    const url = a.url ?? a.Url ?? '';
    const sid =
      a.solicitacaoBriefingArquivoId ?? a.SolicitacaoBriefingArquivoId ?? idx;
    const isImg = isImageFileName(nomeArquivo);
    return {
      id: `saved-${sid}`,
      nomeArquivo,
      remoteUrl: url,
      previewUrl: isImg ? url : null,
      persisted: true,
    };
  });
};

/** ApiResponse do Chat ou fallback string (API AI). */
const extractBriefingLoadPayload = (data) => {
  if (data == null) return { texto: '', itens: [] };
  if (typeof data === 'string') {
    return { texto: data, itens: [] };
  }
  const v = data.value ?? data;
  if (v == null) return { texto: '', itens: [] };
  if (typeof v === 'string') {
    return { texto: v, itens: [] };
  }
  if (typeof v !== 'object') {
    return { texto: '', itens: [] };
  }
  const texto = v.briefing ?? v.Briefing ?? v.texto ?? v.Texto ?? '';
  const arquivos = v.arquivos ?? v.Arquivos ?? [];
  return {
    texto,
    itens: mapApiArquivosToItens(arquivos),
  };
};

const revokeItemPreviewIfBlob = (item) => {
  if (item?.file && item.previewUrl) URL.revokeObjectURL(item.previewUrl);
};

const makeFileItemId = (file) =>
  `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 9)}`;

const PAINEL_SPLIT_MIN = 0.12;
const PAINEL_SPLIT_MAX = 0.88;

const BriefingAI = ({ usuarioId: usuarioIdProp } = {}) => {
  const { userData, showLoader } = useContext(Context);
  const history = useHistory();

  useEffect(() => {
    const loginUserData = JSON.parse(
      localStorage.getItem(appConst.STORAGE_NAME)
    );
    if (loginUserData?.nome !== 'DEV') {
      history.push('/acesso-restrito');
    }
  }, []);

  const [texto, setTexto] = useState('');
  const [itens, setItens] = useState([]);
  const [resposta, setResposta] = useState(null);
  const [erro, setErro] = useState('');
  const [externaLoading, setExternaLoading] = useState(false);
  const [externaFeedback, setExternaFeedback] = useState(null);
  const [consultaFeedback, setConsultaFeedback] = useState(null);
  const [painelSplitTopRatio, setPainelSplitTopRatio] = useState(0.5);
  const [painelSplit2Ratio, setPainelSplit2Ratio] = useState(0.67);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [isTranscrevendo, setIsTranscrevendo] = useState(false);

  const painelSplitContainerRef = useRef(null);
  const itensRefUnmount = useRef(itens);
  itensRefUnmount.current = itens;
  const textareaRef = useRef(null);
  const showLoaderRef = useRef(showLoader);
  const ultimoUsuarioCarregadoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioUrlRef = useRef(audioUrl);
  audioUrlRef.current = audioUrl;

  useEffect(() => {
    showLoaderRef.current = showLoader;
  }, [showLoader]);

  const ajustaAlturaTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    // Sempre expande com todo o conteúdo: sem teto, sem rolagem dentro do textarea (rola a página)
    el.style.removeProperty('max-height');
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflow = 'hidden';
  }, []);

  useLayoutEffect(() => {
    ajustaAlturaTextarea();
  }, [texto, ajustaAlturaTextarea]);

  useEffect(() => {
    const onResize = () => ajustaAlturaTextarea();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [ajustaAlturaTextarea]);

  const SPLIT_MIN_GAP = 0.1;

  const atualizaPainelSplitPeloPonteiro = useCallback(
    (e) => {
      const el = painelSplitContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const h = rect.height;
      if (h < 1) return;
      const r = Math.min(
        PAINEL_SPLIT_MAX,
        Math.max(PAINEL_SPLIT_MIN, (e.clientY - rect.top) / h)
      );
      setPainelSplitTopRatio(r);
      setPainelSplit2Ratio((prev) => Math.max(prev, r + SPLIT_MIN_GAP));
    },
    [SPLIT_MIN_GAP]
  );

  const onPainelSplitPointerDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      atualizaPainelSplitPeloPonteiro(e);
    },
    [atualizaPainelSplitPeloPonteiro]
  );

  const onPainelSplitPointerMove = useCallback(
    (e) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
      atualizaPainelSplitPeloPonteiro(e);
    },
    [atualizaPainelSplitPeloPonteiro]
  );

  const onPainelSplitPointerUp = useCallback((e) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const atualizaPainelSplit2PeloPonteiro = useCallback(
    (e) => {
      const el = painelSplitContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const h = rect.height;
      if (h < 1) return;
      const r = Math.min(
        PAINEL_SPLIT_MAX,
        Math.max(PAINEL_SPLIT_MIN, (e.clientY - rect.top) / h)
      );
      setPainelSplit2Ratio(r);
      setPainelSplitTopRatio((prev) => Math.min(prev, r - SPLIT_MIN_GAP));
    },
    [SPLIT_MIN_GAP]
  );

  const onPainelSplit2PointerDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      atualizaPainelSplit2PeloPonteiro(e);
    },
    [atualizaPainelSplit2PeloPonteiro]
  );

  const onPainelSplit2PointerMove = useCallback(
    (e) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
      atualizaPainelSplit2PeloPonteiro(e);
    },
    [atualizaPainelSplit2PeloPonteiro]
  );

  const onPainelSplit2PointerUp = useCallback((e) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const usuarioId = useMemo(() => {
    if (usuarioIdProp != null) return usuarioIdProp;
    const local = JSON.parse(
      localStorage.getItem(appConst.STORAGE_NAME) || 'null'
    );
    return pickUsuarioId(userData, local);
  }, [userData, usuarioIdProp]);

  useEffect(() => {
    return () => {
      itensRefUnmount.current.forEach(revokeItemPreviewIfBlob);
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    };
  }, []);

  const removeArquivo = (id) => {
    setItens((prev) => {
      const item = prev.find((x) => x.id === id);
      revokeItemPreviewIfBlob(item);
      return prev.filter((x) => x.id !== id);
    });
  };

  const onPickFiles = (e) => {
    const { files } = e.target;
    if (!files?.length) return;
    const added = Array.from(files).map((file) => {
      const isImg = isImageFile(file);
      return {
        id: makeFileItemId(file),
        file,
        previewUrl: isImg ? URL.createObjectURL(file) : null,
      };
    });
    setItens((prev) => [...prev, ...added]);
    e.target.value = '';
  };

  const removerAudio = useCallback(() => {
    setAudioBlob(null);
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, []);

  const iniciarGravacao = useCallback(async () => {
    setErro('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      mediaStreamRef.current = stream;
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType || 'audio/webm',
        });
        setAudioBlob(blob);
        setAudioUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (e) {
      setErro(
        'Não foi possível acessar o microfone. Verifique as permissões do navegador.'
      );
    }
  }, []);

  const pararGravacao = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const toggleGravacao = useCallback(() => {
    if (isRecording) {
      pararGravacao();
    } else {
      removerAudio();
      iniciarGravacao();
    }
  }, [isRecording, pararGravacao, removerAudio, iniciarGravacao]);

  const handleTranscrever = async () => {
    if (!audioBlob) return;
    setErro('');
    setIsTranscrevendo(true);
    showLoaderRef.current?.(true);
    try {
      const textoTranscrito = await TRANSCREVER_AUDIO(audioBlob, usuarioId);
      setTexto((prev) =>
        prev.trim() ? `${prev}\n${textoTranscrito}` : textoTranscrito
      );
    } catch (e) {
      setErro(
        e?.response?.data?.message ||
          e?.message ||
          'Não foi possível transcrever o áudio. Tente novamente.'
      );
    } finally {
      setIsTranscrevendo(false);
      showLoaderRef.current?.(false);
    }
  };

  const onEnviar = async () => {
    setErro('');
    if (usuarioId == null) {
      setErro('Não foi possível identificar o usuário. Faça login novamente.');
      return;
    }
    const t = texto.trim();
    if (!t && itens.length === 0) {
      setErro('Escreva o briefing ou anexe ao menos um arquivo.');
      return;
    }

    const formData = new FormData();
    formData.append('briefing', t);
    formData.append('usuarioId', String(usuarioId));
    itens.forEach((item) => {
      if (item.file) {
        formData.append('arquivos', item.file, item.file.name);
      }
    });

    try {
      showLoaderRef.current?.(true);
      const data = await POST_ENVIAR_BRIEFING(formData);
      setResposta((prev) => data);
      const { texto: textoSrv, itens: itensSrv } =
        extractBriefingLoadPayload(data);
      setItens((prev) => {
        prev.forEach(revokeItemPreviewIfBlob);
        return itensSrv;
      });
      setTexto((prev) => (prev === textoSrv ? prev : textoSrv));
    } catch (e) {
      setErro(
        e?.response?.data?.message ||
          e?.message ||
          'Não foi possível enviar o briefing. Tente novamente.'
      );
    } finally {
      showLoaderRef.current?.(false);
    }
  };

  const handleConsultarBriefingAI = async () => {
    if (usuarioId == null) {
      setErro('Não foi possível identificar o usuário. Faça login novamente.');
      return;
    }
    setErro('');
    showLoaderRef.current?.(true);

    try {
      const data = await CONSULTAR_BRIEFING_AI_EXTERNA(usuarioId);
      setConsultaFeedback(data);
    } catch (e) {
      const errText =
        e?.response?.data?.message ||
        e?.message ||
        'Não foi possível consultar o briefing AI.';
      setErro(errText);
    } finally {
      showLoaderRef.current?.(false);
    }
  };

  const handleProcessAiExterna = async () => {
    if (usuarioId == null) {
      setExternaFeedback({
        status: 'error',
        error: 'Não foi possível identificar o usuário. Faça login novamente.',
      });
      return;
    }
    setExternaLoading(true);
    showLoaderRef.current?.(true);
    try {
      const data = await PROCESS_AI_EXTERNA(usuarioId);
      const { message, error, url } = normalizeAiBriefingResponse(data);
      const errTrim = error.trim();
      setExternaFeedback({
        status: errTrim ? 'error' : 'success',
        texto: message.trim(),
        error: errTrim,
        url: url.trim(),
      });
    } catch (e) {
      const body = e?.response?.data;
      const parsedBody = coerceAiBriefingResponseObject(body);
      const parsed =
        parsedBody != null && typeof parsedBody === 'object'
          ? normalizeAiBriefingResponse(parsedBody)
          : null;
      let errText =
        parsed?.error?.trim() ||
        parsed?.message?.trim() ||
        (typeof parsedBody?.message === 'string'
          ? decodeEmbeddedJsonEscapes(parsedBody.message)
          : null) ||
        (typeof parsedBody?.Message === 'string'
          ? decodeEmbeddedJsonEscapes(parsedBody.Message)
          : null) ||
        (typeof body?.message === 'string'
          ? decodeEmbeddedJsonEscapes(body.message)
          : null) ||
        (typeof body?.Message === 'string'
          ? decodeEmbeddedJsonEscapes(body.Message)
          : null) ||
        e?.message ||
        'Não foi possível chamar a API externa.';
      if (typeof errText !== 'string') errText = JSON.stringify(errText);
      setExternaFeedback({
        status: 'error',
        error: errText,
        texto: errText,
      });
    } finally {
      setExternaLoading(false);
      showLoaderRef.current?.(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (usuarioId == null) return;
      if (ultimoUsuarioCarregadoRef.current === usuarioId) return;
      ultimoUsuarioCarregadoRef.current = usuarioId;

      try {
        const data = await GET_ULTIMO_BRIEFING_POR_USUARIO(usuarioId);
        if (cancelled) return;

        const { texto: textoCarregado, itens: itensCarregados } =
          extractBriefingLoadPayload(data);
        setTexto((prev) => (prev === textoCarregado ? prev : textoCarregado));
        setItens(itensCarregados);
        setErro((prev) => (prev === '' ? prev : ''));
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          return;
        }
        setErro(
          e?.response?.data?.message ||
            e?.message ||
            'Não foi possível carregar o briefing.'
        );
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [usuarioId]);

  const podeEnviar = texto.trim().length > 0 || itens.length > 0;
  const comConsulta = consultaFeedback != null;
  const comResposta = Boolean(resposta);
  const comExterna = externaFeedback != null;
  const mostrarPainelDireito = comConsulta || comResposta || comExterna;
  const secoesPainel = [comConsulta, comResposta, comExterna].filter(
    Boolean
  ).length;

  const s1 = painelSplitTopRatio;
  const s2 = Math.max(painelSplit2Ratio, s1 + 0.1);
  // estilos para layout com 3 seções
  const flex3A = { flex: `${s1} 1 0px`, minHeight: 0 };
  const flex3B = { flex: `${s2 - s1} 1 0px`, minHeight: 0 };
  const flex3C = { flex: `${1 - s2} 1 0px`, minHeight: 0 };
  // estilos para layout com 2 seções
  const flexTop = { flex: `${s1} 1 0px`, minHeight: 0 };
  const flexBottom = { flex: `${1 - s1} 1 0px`, minHeight: 0 };

  return (
    <div
      className={
        mostrarPainelDireito
          ? 'briefing-ai-page briefing-ai-page--has-response'
          : 'briefing-ai-page'
      }
    >
      <div className="briefing-ai-page__main">
        <div className="briefing-ai-page__inner">
          <h1 className="briefing-ai-page__headline">
            Digite o briefing para o AI?
          </h1>
          {usuarioId == null && (
            <p className="briefing-ai-page__hint">
              Conecte-se para carregar e salvar seus briefings.
            </p>
          )}

          <div className="briefing-ai-page__bar">
            <div className="briefing-ai-page__input-wrap">
              <textarea
                ref={textareaRef}
                className="briefing-ai-page__textarea"
                rows={1}
                placeholder="Digite aqui"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    onEnviar();
                  }
                }}
              />
            </div>

            <button
              type="button"
              className={
                isRecording
                  ? 'briefing-ai-page__send briefing-ai-page__send--recording'
                  : 'briefing-ai-page__send'
              }
              title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
              onClick={toggleGravacao}
            >
              {isRecording ? (
                <IoMdSquare size={18} />
              ) : (
                <HiOutlineMicrophone size={22} />
              )}
            </button>

            <label
              className="briefing-ai-page__send"
              htmlFor="briefing-ai-file-input"
              title="Upload de arquivo"
            >
              <HiUpload size={22} />
            </label>
          </div>

          <input
            id="briefing-ai-file-input"
            type="file"
            multiple
            className="briefing-ai-page__file-input--visually-hidden"
            onChange={onPickFiles}
          />

          {audioUrl && (
            <div className="briefing-ai-page__audio-preview">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio
                className="briefing-ai-page__audio-player"
                src={audioUrl}
                controls
              />
              <button
                type="button"
                className="briefing-ai-page__chip"
                title="Transcrever áudio"
                onClick={handleTranscrever}
                disabled={isTranscrevendo}
              >
                <IoMdSend size={18} />
                <span>
                  {isTranscrevendo ? 'Transcrevendo…' : 'Transcrever'}
                </span>
              </button>
              <a
                className="briefing-ai-page__audio-remove"
                href={audioUrl}
                download="gravacao.webm"
                title="Baixar gravação"
                aria-label="Baixar gravação"
              >
                <HiOutlineDownload size={18} />
              </a>
              <button
                type="button"
                className="briefing-ai-page__audio-remove"
                title="Remover gravação"
                onClick={removerAudio}
                disabled={isTranscrevendo}
                aria-label="Remover gravação"
              >
                <HiOutlineTrash size={18} />
              </button>
            </div>
          )}

          <div className="briefing-ai-page__chips">
            <button
              type="button"
              className="briefing-ai-page__chip"
              title="Salvar briefing"
              onClick={onEnviar}
              disabled={!podeEnviar}
            >
              <IoMdSend size={18} />
              <span>Salvar briefing</span>
            </button>
            <button
              type="button"
              className="briefing-ai-page__chip"
              title="Consultar briefing AI"
              onClick={handleConsultarBriefingAI}
              disabled={externaLoading}
            >
              <IoMdSend size={18} />
              <span>Consultar briefing AI</span>
            </button>
            <button
              type="button"
              className="briefing-ai-page__chip briefing-ai-page__chip--external"
              title="POST JSON para o endpoint definido em REACT_APP_BRIEFING_EXTERNAL_API_URL"
              onClick={handleProcessAiExterna}
              disabled={externaLoading}
            >
              <HiOutlineExternalLink size={18} aria-hidden />
              <span>Chama API AI</span>
            </button>
          </div>

          {itens.length > 0 && (
            <ul className="briefing-ai-page__previews">
              {itens.map((item) => {
                const nome = item.file?.name ?? item.nomeArquivo ?? 'Arquivo';
                const showImageThumb = Boolean(item.previewUrl);
                return (
                  <li key={item.id} className="briefing-ai-page__preview-item">
                    <div className="briefing-ai-page__preview-card">
                      {showImageThumb ? (
                        <img
                          src={item.previewUrl}
                          alt={nome}
                          className="briefing-ai-page__preview-card-img"
                        />
                      ) : (
                        <div className="briefing-ai-page__preview-card-file">
                          <HiOutlineDocument
                            className="briefing-ai-page__preview-card-icon"
                            size={28}
                            aria-hidden
                          />
                          <span
                            className="briefing-ai-page__preview-card-name"
                            title={nome}
                          >
                            {nome}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        className="briefing-ai-page__preview-card-remove"
                        onClick={() => removeArquivo(item.id)}
                        aria-label={`Remover ${nome}`}
                      >
                        ×
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {erro && <div className="briefing-ai-page__error">{erro}</div>}
        </div>
      </div>

      {mostrarPainelDireito && (
        <aside
          className="briefing-ai-page__response-panel"
          aria-label="Consulta AI, resposta do briefing e API externa"
        >
          {secoesPainel === 3 ? (
            <div
              ref={painelSplitContainerRef}
              className="briefing-ai-page__panel-split"
            >
              <PainelSecaoConsulta
                consultaFeedback={consultaFeedback}
                sectionStyle={flex3A}
                onFechar={() => setConsultaFeedback(null)}
              />
              <div
                className="briefing-ai-page__panel-splitter"
                role="separator"
                aria-orientation="horizontal"
                aria-label="Ajustar entre Consulta AI e Resposta"
                onPointerDown={onPainelSplitPointerDown}
                onPointerMove={onPainelSplitPointerMove}
                onPointerUp={onPainelSplitPointerUp}
                onPointerCancel={onPainelSplitPointerUp}
              />
              <PainelSecaoCadastroBriefing
                resposta={resposta}
                sectionStyle={flex3B}
                onFechar={() => setResposta(null)}
              />
              <div
                className="briefing-ai-page__panel-splitter"
                role="separator"
                aria-orientation="horizontal"
                aria-label="Ajustar entre Resposta e API externa"
                onPointerDown={onPainelSplit2PointerDown}
                onPointerMove={onPainelSplit2PointerMove}
                onPointerUp={onPainelSplit2PointerUp}
                onPointerCancel={onPainelSplit2PointerUp}
              />
              <PainelSecaoExterna
                externaFeedback={externaFeedback}
                sectionStyle={flex3C}
                onFechar={() => setExternaFeedback(null)}
              />
            </div>
          ) : secoesPainel === 2 ? (
            <div
              ref={painelSplitContainerRef}
              className="briefing-ai-page__panel-split"
            >
              {comConsulta ? (
                <PainelSecaoConsulta
                  consultaFeedback={consultaFeedback}
                  sectionStyle={flexTop}
                  onFechar={() => setConsultaFeedback(null)}
                />
              ) : (
                <PainelSecaoCadastroBriefing
                  resposta={resposta}
                  sectionStyle={flexTop}
                  onFechar={() => setResposta(null)}
                />
              )}
              <div
                className="briefing-ai-page__panel-splitter"
                role="separator"
                aria-orientation="horizontal"
                aria-label="Ajustar tamanho entre seções"
                onPointerDown={onPainelSplitPointerDown}
                onPointerMove={onPainelSplitPointerMove}
                onPointerUp={onPainelSplitPointerUp}
                onPointerCancel={onPainelSplitPointerUp}
              />
              {comConsulta && comResposta ? (
                <PainelSecaoCadastroBriefing
                  resposta={resposta}
                  sectionStyle={flexBottom}
                  onFechar={() => setResposta(null)}
                />
              ) : (
                <PainelSecaoExterna
                  externaFeedback={externaFeedback}
                  sectionStyle={flexBottom}
                  onFechar={() => setExternaFeedback(null)}
                />
              )}
            </div>
          ) : (
            <>
              {comConsulta && (
                <PainelSecaoConsulta
                  consultaFeedback={consultaFeedback}
                  onFechar={() => setConsultaFeedback(null)}
                />
              )}
              {comResposta && (
                <PainelSecaoCadastroBriefing
                  resposta={resposta}
                  onFechar={() => setResposta(null)}
                />
              )}
              {comExterna && (
                <PainelSecaoExterna
                  externaFeedback={externaFeedback}
                  onFechar={() => setExternaFeedback(null)}
                />
              )}
            </>
          )}
        </aside>
      )}
    </div>
  );
};

export default BriefingAI;
